import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, User, Calendar, Clock, Gift, DollarSign, TrendingUp,
  Send, HelpCircle, Inbox, Users, Building, UserCheck, BookOpen, FileText,
  Star, ChevronLeft, ChevronRight, ChevronDown, Cpu,
  FolderKanban, Package, Megaphone,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { setMobileMenuOpen, toggleSidebarCollapsed } from '@/redux/slices/ui/uiSlice';
import { ROLES } from '@/constants';
import qubiqonLogo from '@/assets/images/qubiqon-logo-login.svg';
import { useEffect, useState } from 'react';

const empNav = [
  { to: '/my-space',    label: 'My Space',       icon: LayoutDashboard },
  { to: '/leave',       label: 'Leave',           icon: Calendar },
  // { to: '/attendance',  label: 'Attendance',      icon: Clock },
  { to: '/holidays',    label: 'Holidays',        icon: Gift },
  // { to: '/payroll',     label: 'Payroll',         icon: DollarSign },
  { to: '/requests',    label: 'Requests',        icon: Send },
  { to: '/helpdesk',    label: 'Helpdesk',        icon: HelpCircle },
  { to: '/req-centre',  label: 'Request Centre',  icon: Inbox, managerOnly: true },
  { to: '/hierarchy',   label: 'Org Hierarchy',   icon: Users },
  { to: 'https://finance.qubiqon.io', label: 'Finance', icon: DollarSign },
  { to: 'https://project.qubiqon.io', label: 'PM Tool', icon: FolderKanban },
  { to: 'https://performance.qubiqon.io', label: 'Performance', icon: TrendingUp },
  { to: 'https://salmon-beach-08c6f9600.7.azurestaticapps.net', label: 'Material Request', icon: FileText },
];

const opsNav = [
  { to: '/ops/employees',    label: 'Employee Management', icon: UserCheck },
  // { to: '/ops/payroll',      label: 'Payroll',       icon: DollarSign },
  { to: '/ops/leave-ledger', label: 'Leave Ledger',  icon: BookOpen },
  { to: '/ops/letters',      label: 'Letters',       icon: FileText },
  { to: '/ops/assets',       label: 'Assets',        icon: Package },
  { to: 'https://performance.qubiqon.io',  label: 'Performance',   icon: Star },
];

const cfgNav = [
  { to: '/config/organizations', label: 'Organization', icon: Building, alsoActiveOn: '/config/org' },
  { to: '/config/holiday-calendar', label: 'Holiday Calendar', icon: Gift },
  { to: '/config/empdata',     label: 'Employee Data',   icon: User },
  { to: '/config/leave',       label: 'Leave & Holiday', icon: Calendar },
  // { to: '/config/payroll',     label: 'Payroll',         icon: DollarSign },
  // { to: 'https://performance.qubiqon.io', label: 'Performance',     icon: TrendingUp },
  { to: '/config/assets',      label: 'Assets',          icon: Cpu },
  { to: '/config/helpdesk',    label: 'HelpDesk',        icon: HelpCircle },
  { to: '/config/notices',     label: 'Notice Board',    icon: Megaphone },
  { to: '/config/letters',     label: 'Letters',         icon: FileText },
];

type NavItemDef = {
  to: string;
  label: string;
  icon: React.ElementType;
  alsoActiveOn?: string;
  managerOnly?: boolean;
};

type AccordionSection = 'ops' | 'config';

function isNavItemActive(pathname: string, item: NavItemDef): boolean {
  if (item.to.startsWith('http')) return false;
  if (pathname === item.to || pathname.startsWith(`${item.to}/`)) return true;
  if (item.alsoActiveOn && pathname.startsWith(item.alsoActiveOn)) return true;
  return false;
}

function getActiveSection(pathname: string): AccordionSection | null {
  if (opsNav.some((item) => isNavItemActive(pathname, item))) return 'ops';
  if (cfgNav.some((item) => isNavItemActive(pathname, item))) return 'config';
  return null;
}

export function Sidebar() {
  const { user, hasRole, isManager } = useAuth();
  const { branding } = useBranding();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const mobileMenuOpen = useAppSelector((s) => s.ui.mobileMenuOpen);
  const sidebarCollapsed = useAppSelector((s) => s.ui.sidebarCollapsed);
  const canOpenManagerPages = isManager || user?.role !== ROLES.EMPLOYEE;
  const visibleEmpNav = empNav.filter((item) => {
    if (item.managerOnly && !canOpenManagerPages) return false;
    if (item.label === 'Finance' && user?.hasFinanceAccess === false) return false;
    return true;
  });

  const [expandedSection, setExpandedSection] = useState<AccordionSection | null>(() =>
    getActiveSection(location.pathname),
  );

  useEffect(() => {
    const active = getActiveSection(location.pathname);
    if (active) setExpandedSection(active);
  }, [location.pathname]);

  const isAdminOrHr = hasRole(ROLES.ADMIN) || hasRole(ROLES.HR);

  const closeMobileMenu = () => {
    if (mobileMenuOpen) dispatch(setMobileMenuOpen(false));
  };

  const navLinkPadding = sidebarCollapsed ? 'px-2 justify-center' : 'px-4';

  const NavItem = ({
    to,
    label,
    icon: Icon,
    alsoActiveOn,
  }: NavItemDef) => {
    const iconEl = <Icon size={18} className="shrink-0" strokeWidth={2} />;

    if (to.startsWith('http')) {
      return (
        <a
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          title={sidebarCollapsed ? label : undefined}
          onClick={closeMobileMenu}
          className={`sidebar-nav-link flex items-center w-full transition-colors hover:bg-surface-muted py-2.5 mx-0 ${navLinkPadding}`}
        >
          {iconEl}
          {!sidebarCollapsed && <span className="truncate text-sm">{label}</span>}
        </a>
      );
    }

    return (
      <NavLink
        to={to}
        title={sidebarCollapsed ? label : undefined}
        onClick={closeMobileMenu}
        className={({ isActive }) => {
          const active =
            isActive || (alsoActiveOn ? location.pathname.startsWith(alsoActiveOn) : false);
          return `sidebar-nav-link flex items-center w-full transition-colors border-l-4 ${
            active
              ? 'active bg-brand-primary/10 text-brand-primary font-semibold border-brand-primary'
              : 'hover:bg-surface-muted border-transparent'
          } py-2.5 mx-0 ${navLinkPadding}`;
        }}
      >
        {iconEl}
        {!sidebarCollapsed && <span className="truncate text-sm">{label}</span>}
      </NavLink>
    );
  };

  const CollapsibleSection = ({
    label,
    items,
    expanded,
    onToggle,
  }: {
    label: string;
    items: NavItemDef[];
    expanded: boolean;
    onToggle: () => void;
  }) => {
    if (sidebarCollapsed) {
      return (
        <div className="mb-1">
          {items.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
        </div>
      );
    }

    return (
      <div className="mb-1">
        <button
          onClick={onToggle}
          className="flex items-center justify-between w-full px-3 pt-3 pb-1 text-2xs font-bold text-text-muted uppercase tracking-widest hover:text-text-primary transition-colors"
        >
          <span>{label}</span>
          <ChevronDown
            size={12}
            className={`shrink-0 transition-transform duration-300 ease-in-out ${
              expanded ? 'rotate-180' : ''
            }`}
          />
        </button>
        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
            expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
        >
          <div className="overflow-hidden">
            {items.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const Section = ({ label, items }: { label: string; items: NavItemDef[] }) => (
    <div className="mb-1">
      {!sidebarCollapsed && (
        <div className="px-3 pt-3 pb-1 text-2xs font-bold text-text-muted uppercase tracking-widest">
          {label}
        </div>
      )}
      {items.map((item) => (
        <NavItem key={item.to} {...item} />
      ))}
    </div>
  );

  return (
    <aside
      className={`bg-surface flex flex-col shrink-0 h-screen border-r border-border fixed inset-y-0 left-0 z-40 w-sidebar transition-[width,transform] duration-300 md:relative md:translate-x-0 md:shadow-none ${
        sidebarCollapsed ? 'md:w-sidebar-collapsed' : 'md:w-sidebar'
      } ${mobileMenuOpen ? 'translate-x-0 shadow-sm' : '-translate-x-full'}`}
    >
      {/* Brand + desktop collapse toggle */}
      <div
        className={`flex shrink-0 w-full ${
          sidebarCollapsed
            ? 'flex-col items-center py-3 px-1 gap-2'
            : 'flex-row items-center py-3 pl-2 pr-1 gap-1'
        }`}
      >
        <div
          className={`flex items-center justify-center overflow-hidden ${
            sidebarCollapsed ? 'w-full' : 'flex-1 min-w-0'
          }`}
        >
          <img
            src={branding.companyLogo ?? qubiqonLogo}
            alt="Organization logo"
            className={`object-contain shrink-0 ${
              sidebarCollapsed ? 'h-6 w-6' : 'h-6 w-auto max-w-[140px]'
            }`}
          />
        </div>
        <button
          type="button"
          onClick={() => dispatch(toggleSidebarCollapsed())}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!sidebarCollapsed}
          className="hidden md:flex items-center shrink-0 p-1.5 rounded-lg text-text-muted hover:bg-surface-muted hover:text-text-primary transition-colors"
        >
          {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-1.5 py-2 scrollbar-none">
        <Section label="My Workspace" items={visibleEmpNav} />
        {isAdminOrHr && (
          <>
            <CollapsibleSection
              label="HR Operations"
              items={opsNav}
              expanded={expandedSection === 'ops'}
              onToggle={() =>
                setExpandedSection((prev) => (prev === 'ops' ? null : 'ops'))
              }
            />
            <CollapsibleSection
              label="Config Module"
              items={cfgNav}
              expanded={expandedSection === 'config'}
              onToggle={() =>
                setExpandedSection((prev) => (prev === 'config' ? null : 'config'))
              }
            />
          </>
        )}
      </nav>
    </aside>
  );
}
